import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface Investment {
  id: string;
  investment_type_id: string;
  balance: number;
  currency: string;
  convertedBalance?: number;
  investment_type?: {
    id: string;
    name: string;
  };
}

interface TypeSummaryCardProps {
  typeId: string;
  investments: Investment[];
  currency: string;
  isLoading: boolean;
  typeName?: string;
  filterByType?: boolean;
}

/**
 * TypeSummaryCard component displays metrics for a specific investment type
 * including total value and count of investments.
 * @param typeName - The investment type name to display metrics for (Optional). If not provided, the typeId is used
 * @param typeId - The investment type ID to display metrics for
 * @param investments - Array of all investment accounts (will be filtered by typeId)
 * @param currency - The selected currency for display
 * @param isLoading - Loading state indicator
 * @param filterByType - Whether to filter the investment types by the type or collect aggregate across all types (Optional). By default this is true
 * 
 * @example
 * <TypeSummaryCard
 *   typeName="TypeName"
 *   typeId="type-123"
 *   investments={allInvestments}
 *   currency="USD"
 *   isLoading={false}
 * />
 */
export function TypeSummaryCard({
  typeId,
  investments,
  currency,
  isLoading,
  typeName,
  filterByType = true,
}: TypeSummaryCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Type Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-8 w-full" />
        </CardContent>
      </Card>
    );
  }

  // Filter investments by type
  const filteredInvestments = filterByType ? investments.filter(
    inv => inv.investment_type_id === typeId
  ) : investments;

  // Calculate total value for this type using converted balances if available
  const totalValue = filteredInvestments.reduce((sum, inv) => {
    const balanceToUse = inv.convertedBalance !== undefined ? inv.convertedBalance : inv.balance;
    return sum + balanceToUse;
  }, 0);

  const investmentCount = filteredInvestments.length;
  const investmentTypeName = typeName || filteredInvestments[0]?.investment_type?.name || 'Unknown Type';

  const formatCurrency = (amount: number, curr: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: curr,
    }).format(amount);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{investmentTypeName}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4" aria-live="polite" aria-atomic="true">
        <div>
          <p className="text-sm text-muted-foreground">Total Value</p>
          <p className="text-3xl font-bold">
            {formatCurrency(totalValue, currency)}
          </p>
        </div>
        
        <div>
          <p className="text-sm text-muted-foreground">Number of Investments</p>
          <p className="text-2xl font-semibold">{investmentCount}</p>
        </div>

        {investmentCount === 0 && (
          <div className="text-sm text-muted-foreground">
            No investments in this category yet
          </div>
        )}
      </CardContent>
    </Card>
  );
}
